if (!customElements.get('section-home-reviews-v2')) {
  customElements.define(
    'section-home-reviews-v2',
    class HomeReviewsV2 extends HTMLElement {
      static MOBILE_MQ = '(max-width: 767px)';

      constructor() {
        super();
        this._initialized = false;
        this._resizeTimer = null;
        this._onResize = this.handleResize.bind(this);

        window.addEventListener('DOMContentLoaded', () => {
          this.observeElement(this, this.handleInViewport.bind(this));
        });
        window.addEventListener('resize', this._onResize);
      }

      handleInViewport() {
        if (this._initialized) return;
        this._initialized = true;
        this.init();
      }

      observeElement(element, callback) {
        this.observer = new IntersectionObserver(
          (entries, observerInstance) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                callback();
                observerInstance.unobserve(entry.target);
              }
            });
          },
          {
            root: null,
            threshold: 0.5,
          }
        );

        if (this.isElementInViewport(element)) {
          callback();
          return;
        }
        this.observer.observe(element);
      }

      isElementInViewport(el) {
        const rect = el.getBoundingClientRect();
        return (
          rect.top >= -el.offsetHeight / 2 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) +
              el.offsetHeight / 2
        );
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        window.removeEventListener('resize', this._onResize);
        clearTimeout(this._resizeTimer);
        if (this._$reviewBox) {
          this._$reviewBox.off('init setPosition reInit', this._onSlickLayout);
        }
      }

      isMobile() {
        return window.matchMedia(HomeReviewsV2.MOBILE_MQ).matches;
      }

      handleResize() {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          this.equalizeSlideHeights();
        }, 150);
      }

      measureNaturalHeight(element) {
        const width = element.getBoundingClientRect().width || element.offsetWidth;
        if (!width) return element.scrollHeight;

        const clone = element.cloneNode(true);
        clone.style.cssText = `position:absolute;left:-9999px;top:0;visibility:hidden;height:auto;min-height:0;width:${width}px;pointer-events:none;`;
        this.appendChild(clone);
        const height = clone.offsetHeight;
        clone.remove();
        return height;
      }

      resetSlideHeights() {
        this.querySelectorAll('.review-slide__content, .review-slide__inner').forEach((el) => {
          el.style.minHeight = '';
          el.style.height = '';
        });

        const slickList = this.querySelector('.slick-list');
        const slickTrack = this.querySelector('.slick-track');
        if (slickList) slickList.style.height = '';
        if (slickTrack) slickTrack.style.height = '';
      }

      equalizeSlideHeights() {
        const pcSlides = parseInt(this.getAttribute('data-pc-slides'), 10) || 1;
        if (pcSlides > 1 || !this.isMobile()) {
          this.resetSlideHeights();
          return;
        }

        const contents = this.querySelectorAll('.review-slide__content');
        const inners = this.querySelectorAll('.review-slide__inner');
        if (!contents.length) return;

        this.resetSlideHeights();

        requestAnimationFrame(() => {
          let maxContentHeight = 0;
          contents.forEach((content) => {
            maxContentHeight = Math.max(
              maxContentHeight,
              this.measureNaturalHeight(content)
            );
          });

          if (maxContentHeight <= 0) return;

          contents.forEach((content) => {
            content.style.minHeight = `${maxContentHeight}px`;
          });

          requestAnimationFrame(() => {
            let maxInnerHeight = 0;
            inners.forEach((inner) => {
              maxInnerHeight = Math.max(maxInnerHeight, inner.offsetHeight);
            });

            if (maxInnerHeight <= 0) return;

            inners.forEach((inner) => {
              inner.style.minHeight = `${maxInnerHeight}px`;
            });

            const slickList = this.querySelector('.slick-list');
            if (slickList) {
              slickList.style.height = `${maxInnerHeight}px`;
            }
          });
        });
      }

      init() {
        const reviewBox = this.querySelector('.review-box');
        if (!reviewBox || typeof $ === 'undefined' || !$.fn.slick) return;

        const loopPlay = reviewBox.getAttribute('data-loop');
        const pcSlides = parseInt(this.getAttribute('data-pc-slides'), 10) || 1;
        const padSlides = Math.max(1, pcSlides - 1);
        const isGrid = pcSlides > 1;
        const arrowSvgPrev =
          '<svg aria-hidden="true" role="img" aria-labelledby="home-review-v2prev" xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none"><title id="home-review-v2prev">Home Review Prev</title>  <path d="M0.295497 8.07112C-0.0950267 7.6806 -0.0950268 7.04743 0.295497 6.65691L6.65946 0.292945C7.04998 -0.0975789 7.68315 -0.097579 8.07367 0.292945C8.4642 0.68347 8.4642 1.31663 8.07367 1.70716L2.41682 7.36401L8.07367 13.0209C8.4642 13.4114 8.4642 14.0446 8.07367 14.4351C7.68315 14.8256 7.04998 14.8256 6.65946 14.4351L0.295497 8.07112ZM2.33594 7.36401L2.33594 8.36401L1.0026 8.36401L1.0026 7.36401L1.0026 6.36401L2.33594 6.36401L2.33594 7.36401Z" fill="currentColor"/></svg>';
        const arrowSvgNext =
          '<svg aria-hidden="true" role="img" aria-labelledby="home-review-v2next" xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none"><title id="home-review-v2next">Home Review Next</title><path d="M8.07169 8.07112C8.46221 7.6806 8.46221 7.04743 8.07169 6.65691L1.70773 0.292945C1.31721 -0.0975789 0.68404 -0.097579 0.293516 0.292945C-0.0970081 0.68347 -0.0970082 1.31663 0.293516 1.70716L5.95037 7.36401L0.293515 13.0209C-0.0970092 13.4114 -0.0970093 14.0446 0.293515 14.4351C0.684039 14.8256 1.3172 14.8256 1.70773 14.4351L8.07169 8.07112ZM6.03125 7.36401L6.03125 8.36401L7.36458 8.36401L7.36458 7.36401L7.36458 6.36401L6.03125 6.36401L6.03125 7.36401Z" fill="currentColor"/></svg>';

        const infinite = loopPlay === 'true' || loopPlay === true;
        const prevArrow = `<button type="button" class="slick-prev home-reviews-v2-arrow" aria-label="Previous">${arrowSvgPrev}<span class="sr-only">Previous</span></button>`;
        const nextArrow = `<button type="button" class="slick-next home-reviews-v2-arrow" aria-label="Next">${arrowSvgNext}<span class="sr-only">Next</span></button>`;

        this._$reviewBox = $(reviewBox);
        this._onSlickLayout = () => {
          this.equalizeSlideHeights();
        };

        this._$reviewBox.on('init setPosition reInit', this._onSlickLayout);
        this._$reviewBox.on('init', (event, slick) => {
          const w = window.innerWidth;
          const shown = w >= 1200 ? pcSlides : w >= 750 ? padSlides : 1;
          if (isGrid && slick.slideCount <= shown) {
            slick.unslick();
          }
        });

        this._$reviewBox.slick({
          infinite,
          slidesToShow: isGrid ? pcSlides : 1,
          slidesToScroll: 1,
          arrows: true,
          dots: false,
          autoplay: false,
          fade: !isGrid,
          adaptiveHeight: false,
          prevArrow,
          nextArrow,
          responsive: isGrid
            ? [
                { breakpoint: 1200, settings: { slidesToShow: padSlides } },
                { breakpoint: 750, settings: { slidesToShow: 1, arrows: true, dots: true } },
              ]
            : [
                { breakpoint: 768, settings: { arrows: true, dots: true, adaptiveHeight: false } },
              ],
        });

        this.equalizeSlideHeights();

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.equalizeSlideHeights());
        }
      }
    }
  );
}
